import React, { useState } from 'react';
import { Trash2, UserPlus, RefreshCw, MessageCircle, Mail, X, Archive } from 'lucide-react';
import ConfirmModal from './ConfirmModal';
import { useAuth } from '../context/AuthContext';

// Shared bulk-action toolbar (Feature 3). Buttons are gated by the RBAC
// matrix: assign/status need `bulk`, delete needs `bulk` + `delete`.
// onWA/onEmail receive no args (the page derives targets from its selection).
// onArchive(archive: boolean) — pass `archivedView` when showing the archived
// list so the button flips to "Unarchive".
// `extra` renders module-specific controls (e.g. Billing's set-due-date or
// receipt mode/date edits) inline in the same bar — pages pass elements
// instead of forking the component. It receives { ask } so custom actions get
// the same confirm dialog. Rendered after the built-in slots, before Clear.
export default function BulkActionBar({ module, count, staff = [], statuses = [], onAssign, onStatus, onDelete, onArchive, archivedView = false, onWA, onEmail, onClear, extra }) {
  const { can } = useAuth();
  const [assignee, setAssignee] = useState('');
  const [status, setStatus] = useState('');
  const [confirm, setConfirm] = useState(null); // { message, action, variant }

  if (!count) return null;
  const canBulk = can(module, 'bulk');
  const canDel = canBulk && can(module, 'delete');

  const ask = (message, action, variant = 'update') => setConfirm({ message, action, variant });

  return (
    <div className="flex flex-wrap items-center gap-2 bg-blue-50 border border-blue-200 rounded-xl px-3 py-2 mb-3">
      <span className="text-sm font-semibold text-[#1e3a5f]">{count} selected</span>

      {canBulk && onAssign && (
        <span className="flex items-center gap-1">
          <select value={assignee} onChange={e => setAssignee(e.target.value)} className="input-field text-xs py-1 w-36">
            <option value="">Assign to…</option>
            {staff.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
          <button disabled={!assignee}
            onClick={() => ask(`Assign ${count} record${count !== 1 ? 's' : ''} to ${staff.find(s => String(s.id) === String(assignee))?.name}?`, () => { onAssign(parseInt(assignee)); setAssignee(''); }, 'assign')}
            className="text-xs px-2 py-1.5 rounded-lg bg-[#1e3a5f] text-white disabled:opacity-40 flex items-center gap-1"><UserPlus size={12} /> Assign</button>
        </span>
      )}

      {canBulk && onStatus && (
        <span className="flex items-center gap-1">
          <select value={status} onChange={e => setStatus(e.target.value)} className="input-field text-xs py-1 w-36">
            <option value="">Set status…</option>
            {statuses.map(s => <option key={s}>{s}</option>)}
          </select>
          <button disabled={!status}
            onClick={() => ask(`Update status to "${status}" for ${count} record${count !== 1 ? 's' : ''}?`, () => { onStatus(status); setStatus(''); })}
            className="text-xs px-2 py-1.5 rounded-lg bg-[#1e3a5f] text-white disabled:opacity-40 flex items-center gap-1"><RefreshCw size={12} /> Update</button>
        </span>
      )}

      {onWA && (
        <button onClick={onWA} className="flex items-center gap-1 text-xs text-green-600 border border-green-200 rounded-lg px-2 py-1.5 hover:bg-green-50">
          <MessageCircle size={13} /> WhatsApp
        </button>
      )}
      {onEmail && (
        <button onClick={onEmail} className="flex items-center gap-1 text-xs text-blue-600 border border-blue-200 rounded-lg px-2 py-1.5 hover:bg-blue-50">
          <Mail size={13} /> Email
        </button>
      )}
      {canBulk && onArchive && (
        <button onClick={() => ask(
          archivedView
            ? `Unarchive ${count} record${count !== 1 ? 's' : ''}?`
            : `Archive ${count} record${count !== 1 ? 's' : ''}? They move out of the default view (reversible).`,
          () => onArchive(!archivedView), 'update')}
          className="flex items-center gap-1 px-2 py-1.5 border border-amber-300 text-amber-700 rounded-lg text-xs hover:bg-amber-50">
          <Archive size={13} /> {archivedView ? 'Unarchive' : 'Archive'}
        </button>
      )}
      {canDel && onDelete && (
        <button onClick={() => ask(`Delete ${count} record${count !== 1 ? 's' : ''}? This cannot be undone.`, onDelete, 'delete')}
          className="flex items-center gap-1 px-2 py-1.5 bg-red-600 text-white rounded-lg text-xs"><Trash2 size={13} /> Delete</button>
      )}
      {canBulk && typeof extra === 'function' ? extra({ ask, count }) : extra}
      <button onClick={onClear} className="ml-auto text-gray-400 hover:text-gray-600" title="Clear selection"><X size={15} /></button>

      {confirm && (
        <ConfirmModal
          variant={confirm.variant}
          message={confirm.message}
          onConfirm={() => { confirm.action(); setConfirm(null); }}
          onCancel={() => setConfirm(null)}
        />
      )}
    </div>
  );
}
